import { Stack, Typography, Tooltip, CircularProgress, IconButton, Divider, Checkbox } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useState, FC } from 'react';
import { CheckboxProps, NotificationExtended } from '..';
import { DeleteIcon } from '../../../images/svg/deleteIcon';
import { ReadIcon } from '../../../images/svg/readIcon';
import { useAppDispatch } from '../../../redux';
import { useDeleteNotificationMutation, useUpdateNotificationMutation } from '../../../redux/services';
import { openConfirmationDialog, closeConfirmationDialog } from '../../../redux/slices/confirmationDialog';
import { closeDialog } from '../../../redux/slices/dialog';
import { UserAvatar } from '../../userAvatar';
import { notificationHeadings } from '../utils';

interface INotificationCard {
    notification: NotificationExtended,
    refetch: () => void,
    checkboxProps?: CheckboxProps,
}

const NotificationCard: FC<INotificationCard> = ({ notification, refetch, checkboxProps }) => {
    const { include = false, checkboxHandler = () => [] } = checkboxProps || {}
    const dispatch = useAppDispatch();

    const [deleteNotifications, deleteResponse] = useDeleteNotificationMutation();
    const [markAsRead, readResponse] = useUpdateNotificationMutation();

    const [markingId, setMarkingId] = useState<string | null>(null);

    const handleDelete = async (id: string) => {
        setMarkingId(id)
        try {
            await deleteNotifications(id).unwrap();
            refetch();
        } catch (error: any) {
            let errorMessage: string = error?.data?.title ?? `Unable to delete the notification!`;
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            dispatch(closeDialog());
            setMarkingId(null);
        }
    }

    const initiateDeleteNotification = (id: string) => {
        dispatch(
            openConfirmationDialog({
                title: "Delete Operation",
                body: `Are you sure you want to delete the notification?`,
                positiveBtn: "Delete",
                negativeBtn: "Cancel",
                onOk: () => {
                    handleDelete(id);
                    dispatch(closeConfirmationDialog());
                },
                onNegativeBtn: () => dispatch(closeConfirmationDialog()),
            })
        );
    };

    const handleRead = async (id: string) => {
        setMarkingId(id);
        try {
            await markAsRead({ id, status: "read" }).unwrap();
            refetch();
        } catch (error: any) {
            let errorMessage: string = error?.data?.title ?? `Unable to mark it as read!`;
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            setMarkingId(null);
        }
    }

    return (
        <Stack
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={1}
            sx={{
                p: 2,
                bgcolor: notification?.include
                    ? "rgba(92, 45, 179, 0.06)"
                    : !notification?.delivery?.in_app?.read_time
                        ? "rgba(92, 45, 179, 0.03)"
                        : undefined
            }}
        >
            <Stack direction="row" gap={1}>
                {include &&
                    <Checkbox
                        size="small"
                        checked={notification?.include}
                        onChange={() => {
                            checkboxHandler(notification._id)
                        }}
                    />
                }

                {/* User Avatar */}
                <UserAvatar
                    userId={notification?.created_user_id}
                    name={`${notification?.created_user ?? ""}`}
                    sx={{ width: 30, height: 30, fontSize: "0.65rem", mt: 0.5 }}
                />

                {/* Notification text content */}
                <Stack>
                    <Stack direction="row" alignItems="center" justifyContent={"space-between"} gap={1}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                {notificationHeadings[notification?.event?.type]}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {moment(notification?.created_time).fromNow()}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography
                        sx={{ lineHeight: 1.3, mt: 0.5 }}
                        component="div"
                        dangerouslySetInnerHTML={{ __html: notification?.message || notification?.delivery?.in_app?.message || "" }}
                        variant="caption"
                        color="textSecondary"
                    />
                </Stack>
            </Stack>

            {!notification?.include &&
                <Stack alignItems={"center"} gap={1} direction={"row"}>
                    {notification?.delivery?.in_app?.read_time
                        ? <></>
                        : <>
                            <Tooltip title={readResponse.isLoading ? "" : "Mark it as read"}>
                                {readResponse.isLoading && markingId === notification?._id ? <CircularProgress size={20} /> : <IconButton size="small" onClick={() => handleRead(notification?._id)}>
                                    <ReadIcon />
                                </IconButton>}
                            </Tooltip>

                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    height: 24,
                                    my: 0.5,
                                    borderColor: 'rgba(0, 0, 0, 0.2)'
                                }}
                            />
                        </>
                    }
                    {deleteResponse.isLoading && markingId === notification?._id ? <CircularProgress size={20} /> :
                        <IconButton size="small" onClick={() => initiateDeleteNotification(notification?._id)}>
                            <DeleteIcon />
                        </IconButton>}
                </Stack>
            }

        </Stack>
    )
}

export { NotificationCard };